<template>
  <div class="products">
    <NavBar :style="backgroundStyle" sticky transparent>
      <template #left>
        <BackButton icon="EF0030" @click="handleExitCatalog" />
      </template>
      <template #center>
        <Tabs :selected-index="currentTabIndex" :on-change="onChangeTab">
          <TabList :style="backgroundStyle">
            <Tab
              v-for="(tab, index) in tabs"
              :key="index"
              :style="backgroundStyle"
            >
              <InitialAvatarIcon
                :class="{
                  'products__tab-icon': true,
                  'products__tab-icon__actived': currentTabIndex === index
                }"
                :icon="tab.icon"
              />
              {{ tab.label }}
            </Tab>
          </TabList>
        </Tabs>
      </template>
    </NavBar>
    <main>
      <component
        :is="step.component"
        :is-builder="isBuilder"
        @select-product="productId => (selectedProductId = productId)"
      />
      <ProductSideSheet
        data-testid="product-side-sheet"
        :is-builder="isBuilder"
        :can-add="canAddProduct"
        :category-selected-product="categorySelectedProduct"
        :product-api-id="selectedProductId"
        :color="productSideSheetColor"
        :is-open="Boolean(selectedProductId)"
        @add-product="emit('add-product', selectedProductId)"
        @remove-product="emit('remove-product', selectedProductId)"
        @close="closeProductDetails"
      />
      <BlockedInvestmentModal
        :is-open="isBlockedInvestment"
        :type="blockInvestmentType"
        @close="isBlockedInvestment = false"
      />
      <ConfirmExitModal
        :is-open="isConfirmModalOpen"
        @close="isConfirmModalOpen = false"
        @confirm="goBackCatalog"
      />
    </main>
  </div>
</template>

<script lang="ts" setup>
import { Component, computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  NavBar,
  Tabs,
  Tab,
  InitialAvatarIcon,
  TabList
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FixedIncomeProducts } from './components/FixedIncomeProducts';
import { GovernmentBondProducts } from './components/GovernmentBondProducts';
import { FundsProducts } from './components/FundsProducts';
import { CustomOperationsProducts } from './components/CustomOperationsProducts';
import { InitialPublicOfferingsProducts } from './components/InitialPublicOfferingsProducts';
import { ConfirmExitModal } from './components/ConfirmExitModal';
import { IProductGetLastState } from '@/modules/catalog/services/products-acquisition';
import ProductSideSheet from './components/ProductSideSheet/ProductSideSheet.vue';
import { BlockedInvestmentModal } from '@/modules/catalog/components';
import { IInvestmentProfile } from '@/types/models';
import { getCustomerInvestmentProfile } from '@/modules/common/services/http/account';
import { CustomerModule } from '@/modules/common/store/CustomerModule';
import { EProductTypes } from '@/modules/catalog/services/products/products-types';
import { useCatalogStore } from '@/modules/catalog/store';

enum EProductsTabsIndexes {
  FixedIncome = 0,
  GovernmentBond = 1,
  Funds = 2,
  CustomOperations = 3,
  InitialPublicOfferings = 4
}

interface Props {
  isLoadingButtonOnDetail?: boolean;
  products?: IProductGetLastState[];
  closeModal?: boolean;
  isBuilder: boolean;
  cameFrom: string;
}
const props = withDefaults(defineProps<Props>(), {
  isLoadingButtonOnDetail: false,
  products: undefined
});

const emit = defineEmits(['add-product', 'remove-product']);

const stepComponent: Record<EProductTypes, Component> = {
  [EProductTypes.FIXED_INCOME]: FixedIncomeProducts,
  [EProductTypes.FUNDS]: FundsProducts,
  [EProductTypes.GOVERNMENT_BOND]: GovernmentBondProducts,
  [EProductTypes.STRUCTURED_OPERATION]: CustomOperationsProducts,
  [EProductTypes.INITIAL_PUBLIC_OFFERING]: InitialPublicOfferingsProducts
};

const colorMapper: Record<EProductTypes, NebraskaColors> = {
  [EProductTypes.FIXED_INCOME]: NebraskaColors.asset04,
  [EProductTypes.GOVERNMENT_BOND]: NebraskaColors.asset06,
  [EProductTypes.FUNDS]: NebraskaColors.asset12,
  [EProductTypes.STRUCTURED_OPERATION]: NebraskaColors.asset02,
  [EProductTypes.INITIAL_PUBLIC_OFFERING]: NebraskaColors.asset08
};

const router = useRouter();
const route = useRoute();

const currentTabIndex = ref(EProductsTabsIndexes.FixedIncome);
const selectedProductId = ref('');
const categorySelectedProduct = ref(route.params.type as EProductTypes);
const isConfirmModalOpen = ref(false);
const isBlockedInvestment = ref(false);
const blockInvestmentType = ref('');
const investmentProfile = ref<IInvestmentProfile>();

async function fetchPersonalProfile() {
  try {
    const data = await getCustomerInvestmentProfile(
      CustomerModule.getCustomerId
    );
    investmentProfile.value = data.investmentProfile;
  } catch (error) {
    console.error('Erro ao buscar perfil de investimento:', error);
  }
}

const tabs = computed(() => {
  const baseTabs = [
    {
      label: 'Renda fixa',
      icon: 'BA0011',
      key: EProductTypes.FIXED_INCOME
    },
    {
      label: 'Tesouro direto',
      icon: 'CA0100',
      key: EProductTypes.GOVERNMENT_BOND
    },
    {
      label: 'Fundos',
      icon: 'EF0110',
      key: EProductTypes.FUNDS
    },
    {
      label: 'Op. personalizadas',
      icon: 'BB0011',
      key: EProductTypes.STRUCTURED_OPERATION
    }
  ];

  if (!props.isBuilder) {
    baseTabs.push({
      label: 'Ofertas públicas',
      icon: 'DA0080',
      key: EProductTypes.INITIAL_PUBLIC_OFFERING
    });
  }

  if (props.isBuilder) {
    const itemsToRemove = [
      EProductTypes.GOVERNMENT_BOND,
      EProductTypes.STRUCTURED_OPERATION
    ];

    return baseTabs.filter(item => !itemsToRemove.includes(item.key));
  }

  return baseTabs;
});

const step = computed(() => {
  return {
    component: stepComponent[currentTab.value.key]
  };
});

const canAddProduct = computed(() => {
  if (props.isBuilder) {
    return Boolean(
      !props.products.find(
        product =>
          product.productId === selectedProductId.value ||
          product.id === selectedProductId.value
      )
    );
  }

  return true;
});

const currentTab = computed(() => {
  return tabs.value[currentTabIndex.value];
});

const backgroundStyle = computed(() => {
  return { backgroundColor: colorMapper[currentTab.value.key] };
});

function onChangeTab(tab: EProductsTabsIndexes) {
  currentTabIndex.value = tab;

  const routerName = props.isBuilder
    ? `catalog/builder/${
        route.params.portfolioId ? route.params.portfolioId + '/' : ''
      }${currentTab.value.key}`
    : `products-view/catalog/${currentTab.value.key}`;

  window.history.pushState({}, '', `/app/#/${routerName}`);

  categorySelectedProduct.value = currentTab.value.key;
}

function handleExitCatalog() {
  if (props.isBuilder) {
    isConfirmModalOpen.value = true;
    return;
  }
  goBackCatalog();
}

function goBackCatalog() {
  if (useCatalogStore().fromUserFlow) {
    router.push({
      name: useCatalogStore().fromUserFlow,
      query: { cameFrom: useCatalogStore().fromUserCameFrom }
    });
  } else {
    const cameFrom = props.cameFrom || 'home';
    router.push({ name: cameFrom });
  }
}

function closeProductDetails() {
  selectedProductId.value = '';

  const url = props.isBuilder
    ? `catalog/builder/${
        route.params.portfolioId ? route.params.portfolioId + '/' : ''
      }`
    : 'products-view/catalog';

  window.history.pushState({}, '', `/app/#/${url}/${currentTab.value.key}`);
}

watch(
  () => props.closeModal,
  closeModal => {
    if (closeModal) {
      selectedProductId.value = '';
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await fetchPersonalProfile();
});

onBeforeMount(async () => {
  const tabIndexType: { [key: string]: number } = {};
  tabs.value.forEach((tab, index) => {
    tabIndexType[tab.key] = index;
  });

  const type = route.params.type || 'fixed-income';
  const key = type;

  currentTabIndex.value = tabIndexType[key];

  if (route.params?.productId) {
    selectedProductId.value = route.params.productId;
  }
});

const productSideSheetColor = computed(() => {
  return colorMapper[currentTab.value.key];
});
</script>

<style lang="less" scoped>
@keyframes change-color {
  0% {
    background-color: transparent;
  }
  100% {
    background-color: @background-hover;
  }
}

.products {
  :deep(.navbar-container__center) {
    max-width: 1008px;
    width: 1008px;
  }

  :deep(.tab-container--actived) {
    color: @element-over-image !important;
    background-color: @background-hover !important;

    &:hover {
      background-color: @background-hover !important;
      color: @element-over-image !important;

      .products__tab-icon {
        color: @element-over-image !important;
      }
    }
  }

  :deep(.tabs-container) {
    background-color: @background-secondary;
  }

  :deep(.tab-container) {
    height: 48px;
    color: @element-alpha;
    flex: unset;
    padding: @size-spacing-x350 @size-spacing-x400 @size-spacing-x350 10px;
    border-bottom: none;
    border-radius: @size-radius-x300;
    margin-right: @size-spacing-x300;

    &:hover {
      animation: change-color 0.25s ease-in;
      background-color: @background-hover;
    }
  }

  .navbar-container {
    border-bottom: none;
  }

  &__tab-icon {
    background-color: transparent;
    color: @element-alpha;

    &__actived {
      color: @element-over-image;
    }
  }
}
</style>
